// components/forms/FileUpload.jsx
import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  IconButton,
  Paper,
  Chip
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  InsertDriveFile as FileIcon,
  Close as CloseIcon
} from '@mui/icons-material';

/**
 * Componente para subida de archivos con previsualización
 * @param {Object} props - Propiedades del componente
 * @param {Function} props.onFileSelected - Función a llamar cuando se seleccionan archivos
 * @param {String} props.accept - Tipos de archivos aceptados (ej: '.pdf,.doc')
 * @param {Number} props.maxFiles - Número máximo de archivos permitidos
 * @param {Number} props.maxSize - Tamaño máximo permitido en bytes
 * @param {Boolean} props.disabled - Si el componente está deshabilitado
 * @returns {JSX.Element} Componente React
 */
const FileUpload = ({
  onFileSelected,
  accept = '*',
  maxFiles = 1,
  maxSize = 10 * 1024 * 1024, // 10MB por defecto
  disabled = false
}) => {
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [error, setError] = useState(null);
  const fileInputRef = React.useRef(null);

  // Manejar selección de archivos
  const handleFileChange = (event) => {
    const files = Array.from(event.target.files);
    setError(null);

    // Validar cantidad de archivos
    if (files.length > maxFiles) {
      setError(`Solo puede seleccionar hasta ${maxFiles} archivo(s)`);
      return;
    }

    // Validar tamaño de archivos
    const oversizedFiles = files.filter(file => file.size > maxSize);
    if (oversizedFiles.length > 0) {
      setError(`Uno o más archivos exceden el tamaño máximo de ${formatFileSize(maxSize)}`);
      return;
    }

    setSelectedFiles(files);
    
    // Llamar al callback con los archivos seleccionados
    if (onFileSelected) {
      onFileSelected(files);
    }
  };

  // Formatear tamaño de archivo
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  // Eliminar un archivo
  const handleRemoveFile = (index) => {
    const newFiles = [...selectedFiles];
    newFiles.splice(index, 1);
    setSelectedFiles(newFiles);
    
    // Llamar al callback con los archivos actualizados
    if (onFileSelected) {
      onFileSelected(newFiles);
    }
    
    // Limpiar el input si no quedan archivos
    if (newFiles.length === 0 && fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Limpiar todos los archivos
  const handleClearFiles = () => {
    setSelectedFiles([]);
    setError(null);
    
    // Limpiar el input
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
    
    // Llamar al callback con array vacío
    if (onFileSelected) {
      onFileSelected([]);
    }
  };

  return (
    <Box>
      <input
        type="file"
        accept={accept}
        onChange={handleFileChange}
        style={{ display: 'none' }}
        multiple={maxFiles > 1}
        ref={fileInputRef}
        disabled={disabled}
      />
      
      <Box display="flex" flexDirection="column" alignItems="center" p={2}>
        <Button
          variant="outlined"
          startIcon={<UploadIcon />}
          onClick={() => fileInputRef.current.click()}
          disabled={disabled}
        >
          Seleccionar archivo{maxFiles > 1 ? 's' : ''}
        </Button>
        
        <Typography variant="caption" color="textSecondary" sx={{ mt: 1 }}>
          {accept === '*' 
            ? 'Todos los formatos aceptados' 
            : `Formatos aceptados: ${accept}`}
        </Typography>
        
        <Typography variant="caption" color="textSecondary">
          Tamaño máximo: {formatFileSize(maxSize)}
        </Typography>
      </Box>
      
      {error && (
        <Typography color="error" variant="body2" sx={{ mt: 1 }}>
          {error}
        </Typography>
      )}
      
      {selectedFiles.length > 0 && (
        <Paper variant="outlined" sx={{ mt: 2, p: 1 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
            <Typography variant="subtitle2">
              Archivos seleccionados
            </Typography>
            {selectedFiles.length > 0 && (
              <Button size="small" onClick={handleClearFiles} disabled={disabled}>
                Limpiar todos
              </Button>
            )}
          </Box>
          
          <List dense>
            {selectedFiles.map((file, index) => (
              <ListItem key={index}>
                <ListItemIcon>
                  <FileIcon />
                </ListItemIcon>
                <ListItemText
                  primary={file.name}
                  secondary={formatFileSize(file.size)}
                />
                <Chip
                  label={file.type || 'Desconocido'}
                  size="small"
                  variant="outlined"
                  sx={{ mr: 1 }}
                />
                <IconButton
                  edge="end"
                  size="small"
                  onClick={() => handleRemoveFile(index)}
                  disabled={disabled}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </ListItem>
            ))}
          </List>
        </Paper>
      )}
    </Box>
  );
};

export default FileUpload;